"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { useRouter } from "next/navigation";
import { Search } from "lucide-react";

import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DocIcon } from "@/components/docs/icons";
import { DOC_SECTIONS, DOCS_BASE } from "@/app/(marketing)/docs/_config";

// The platform is unknowable on the server, so the server snapshot is
// "not a Mac" and hydration renders "Ctrl K" to match the SSR HTML;
// React then re-renders with the client snapshot. Reading navigator in
// a useState initializer instead made the first client render differ
// from the server's and tripped a hydration mismatch on Mac/iOS.
function subscribe(): () => void {
  return noop;
}

function noop() {
  /* the user agent never changes — nothing to unsubscribe */
}

function getIsMac(): boolean {
  return /mac|iphone|ipad/i.test(navigator.userAgent);
}

function getServerIsMac(): boolean {
  return false;
}

/**
 * The docs search field. The header renders a button styled as a search
 * input; clicking it (or pressing Cmd/Ctrl+K) opens a command palette
 * that filters every doc page by title, section, and description and
 * navigates on select.
 */
export function DocsSearch() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const isMac = useSyncExternalStore(subscribe, getIsMac, getServerIsMac);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    router.push(path);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search documentation"
        className="border-rl-border bg-rl-bg2/70 text-rl-text3 hover:border-rl-border2 hover:text-rl-text2 flex h-9 w-9 max-w-md items-center justify-center gap-2 rounded-lg border text-[0.85rem] transition-colors sm:w-full sm:justify-start sm:px-3"
      >
        <Search size={15} aria-hidden className="shrink-0" />
        <span className="hidden flex-1 truncate text-left sm:block">
          Search documentation
        </span>
        <kbd className="border-rl-border bg-rl-surface/60 text-rl-text3 hidden shrink-0 items-center rounded border px-1.5 py-0.5 font-mono text-[0.65rem] sm:inline-flex">
          {isMac ? "⌘ K" : "Ctrl K"}
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search documentation"
        description="Find a guide in the Raileon documentation"
      >
        <Command>
          <CommandInput placeholder="Search documentation..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {DOC_SECTIONS.map((section) => (
              <CommandGroup key={section.slug} heading={section.title}>
                {section.pages.map((page) => {
                  const path = `${DOCS_BASE}/${section.slug}/${page.slug}`;
                  return (
                    <CommandItem
                      key={path}
                      value={`${section.title} ${page.title} ${page.description ?? ""}`}
                      onSelect={() => go(path)}
                    >
                      <DocIcon
                        name={page.icon ?? section.icon}
                        className="text-rl-text3"
                      />
                      <span>{page.title}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}
